import React from 'react';
import { IonCard, IonCardContent, IonIcon, IonItem } from '@ionic/react';
import { arrowForwardOutline } from 'ionicons/icons';
import { useBalanceVisibility } from '@/hooks/useBalanceVisibility';
import { useAppSelector } from '@/services/redux/hook';
import { useAccountDetailsQuery } from '@/services/api/stellar';
import { useGetQuotesQuery } from '@/services/api/ebioro/ebioro';
import { formatConversion } from '@/services/utils/formatConversion';
import { IconContext } from 'react-icons';
import supportedCurrencies from '@/currencies.json';

const ebioroAuthDomain = import.meta.env.VITE_API_AUTH_DOMAIN || '';

interface AccountTypeCardProps {
    id: string;
    title: string;
    icon: React.ReactNode;
    iconColor: string;
    onClick?: () => void;
    assetType: 'fiat' | 'crypto' | 'rewards' | 'savings';
    isComingSoon?: boolean;
    style?: React.CSSProperties;
}

export default function AccountTypeCard({
    id,
    title,
    icon,
    iconColor,
    onClick,
    assetType,
    isComingSoon = false,
    style
}: AccountTypeCardProps) {
    const { isBalanceHidden } = useBalanceVisibility();
  
    // Redux state
    const walletInfo = useAppSelector((state) => state.walletInfo);
    const supportedAssets = useAppSelector((state) => state.supportedAssets.supportedAssets);
    const userInfo = useAppSelector((state) => state.user);
    const currentCurrency = userInfo.currency_code || 'USD';
    const ebioroAuthToken = useAppSelector((state) => state.sep10AuthToken.tokens[ebioroAuthDomain]);

    // Account details
    const { data: accountDetails } = useAccountDetailsQuery(walletInfo.account_key, { skip: (!walletInfo.has_key) });
    const balances = accountDetails?.details?.balances;

    // Build coins string for quotes API
    const coins = React.useMemo(() => {
        if (!supportedAssets) return '';
        const assetsArray = Array.isArray(supportedAssets) ? supportedAssets : supportedAssets?.supportedAssets || [];
        return assetsArray.map((asset: any) => asset.code).join(',');
    }, [supportedAssets]);

    // Get quotes
    const { data: quotes = {}, isSuccess: quotesIsSuccess } = useGetQuotesQuery(
        { coins: coins, authToken: ebioroAuthToken },
        { skip: !ebioroAuthToken || coins === '', refetchOnMountOrArgChange: true }
    );

    // Calculate balance based on asset type
    const calculateBalance = React.useMemo((): number => {
        if (!quotesIsSuccess || !userInfo || !balances || !supportedAssets) {
            return 0;
        }

        let totalBalance = 0;

        // Check if supportedAssets is an array or object
        const assetsArray = Array.isArray(supportedAssets) ? supportedAssets : supportedAssets?.supportedAssets || [];

        // Filter assets by type
        const filteredAssets = assetsArray.filter((asset: any) => {
            if (assetType === 'fiat') return asset.type === 'fiat';
            if (assetType === 'crypto') return asset.type === 'crypto';
            return false; // rewards and savings return 0 for now
        });

        filteredAssets.forEach((asset: any) => {
            const assetBalance = balances[asset.identifier];
            if (assetBalance && userInfo) {
                const quoteData = quotes[asset.code];
                const currencyCode = userInfo.currency_code?.toLowerCase() || 'usd';
                
                if (quoteData) {
                    // Try both lowercase and uppercase versions of currency code
                    const quoteValue = quoteData[currencyCode] || quoteData[currencyCode.toUpperCase()];
                    
                    if (quoteValue) {
                        const amountToAdd = quoteValue * assetBalance.available.toNumber();
                        totalBalance += amountToAdd;
                    }
                }
            }
        });

        return totalBalance;
    }, [quotesIsSuccess, quotes, userInfo, balances, supportedAssets, assetType]);

    const handleAccountClick = () => {
        if (!isComingSoon && onClick) {
            onClick();
        }
    };

    const formatValue = (): string => {
        if (isBalanceHidden) {
            return '••••';
        }

        if (isComingSoon) {
            return 'Coming Soon';
        }

        return `${getCurrencySymbol(currentCurrency)} ${formatConversion(calculateBalance)}`;
    };

     const getCurrencySymbol = (code: string): string => {
        const currency = supportedCurrencies.find(curr => curr.code === code);
        return currency?.symbol || '$';
      };

    return (
        <IonCard
            button={!isComingSoon}
            onClick={handleAccountClick}
            style={{
                opacity: isComingSoon ? 0.6 : 1,
                cursor: isComingSoon ? 'default' : 'pointer',
                ...style
            }}
        >
            <IonCardContent className="p-4"
             >
                {/* Top: Icon (left) and Arrow (right) using IonItem */}
                <IonItem lines="none" className="px-0 mb-4">
                    <div slot="start" >
                        <IconContext.Provider value={{ 
                            size: "20px",
                            color: iconColor
                        }}>
                            {icon}
                        </IconContext.Provider>
                    </div>
                    {!isComingSoon && (
                        <IonIcon
                            slot="end"
                            icon={arrowForwardOutline}
                            className="w-3 h-3 text-gray-400 dark:text-gray-500"
                            style={{ margin: '0', padding: '0' }}
                        />
                    )}
                </IonItem>

                {/* Account Title */}
                <IonItem lines="none">
                    <div className="mb-4">
                        <p className="text-sm text-gray-700 dark:text-gray-300 font-semibold text-left">
                            {title}
                        </p>
                    </div>
                </IonItem>

                {/* Balance */}
                <IonItem lines="none">
                    <div>
                        <p
                            className="text-gray-900 dark:text-white"
                            style={{
                                fontSize: isComingSoon ? '14px' : '16px',
                                fontWeight: '500',
                                lineHeight: '1.2'
                            }}
                        >
                            {formatValue()}
                        </p>
                      
                    </div>
                </IonItem>
            </IonCardContent>
        </IonCard>
    );
}